import React from 'react';
import PropTypes from 'prop-types';
import { ArrowRight } from 'lucide-react';
import Button from '../Button';
import Hyperlink from '../Hyperlink';
import './NavigationCard.css';

/**
 * NavigationCard — Figma: Header/Navigation Card (3747:12301)
 *
 * Two variants:
 *  - "button" → image + Secondary button below
 *  - "link"   → image + Regular Hyperlink with arrow-right below
 *
 * `card` — the M2 card skin (Figma OVER-LL SideMenu 2388:6010 instances):
 * gray-025 surface, gray-0 hairline, `--radius-card`, `--shadow-card-m2`,
 * small-l padding, image on `--radius-inner-radius`, full width (no 276px cap).
 * `imageFit="contain"` — a cut-out (patch PNG) centred on a white well.
 * `imageAspectRatio` — the image frame's ratio ('3 / 2', '3:2'); default the
 * Figma 276:221 (Crown Student's SideMenu featured card is 3:2 — Crownstudent-MCP 137:6118).
 */
const NavigationCard = ({
  image,
  imageAlt = '',
  type = 'button',
  label,
  href = '#',
  onClick,
  card = false,
  imageFit = 'cover',
  showArrow = true,
  imageAspectRatio,
  className = '',
}) => {
  const rootClasses = [
    'nav-card',
    `nav-card--${type}`,
    card && 'nav-card--card',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={rootClasses}>
      {/* Image */}
      <a
        href={href}
        onClick={onClick}
        className="nav-card__image-wrap"
        style={imageAspectRatio ? { '--nav-card-image-ratio': imageAspectRatio.replace(':', ' / ') } : undefined}
        tabIndex={-1}
        aria-hidden="true"
      >
        <img
          src={image}
          alt={imageAlt}
          className={`nav-card__image${imageFit === 'contain' ? ' nav-card__image--contain' : ''}`}
        />
      </a>

      {/* Action: Button or Link */}
      {type === 'button' && (
        <div className="nav-card__action">
          <Button
            size="secondary"
            variant="main"
            href={href}
            onClick={onClick}
            className="nav-card__button"
          >
            {label}
          </Button>
        </div>
      )}

      {type === 'link' && (
        <Hyperlink
          href={href}
          onClick={onClick}
          variant="regular"
          showIconRight={showArrow}
          iconRight={showArrow ? <ArrowRight size={20} /> : undefined}
          className="nav-card__link"
        >
          {label}
        </Hyperlink>
      )}
    </div>
  );
};

NavigationCard.propTypes = {
  /** Image source URL */
  image: PropTypes.string.isRequired,
  /** Alt text for image */
  imageAlt: PropTypes.string,
  /** Card type — button shows Secondary CTA, link shows Hyperlink with arrow */
  type: PropTypes.oneOf(['button', 'link']),
  /** Label for button or link */
  label: PropTypes.string.isRequired,
  /** URL for button/link/image */
  href: PropTypes.string,
  /** Click handler (for button type) */
  onClick: PropTypes.func,
  /** M2 card skin — surface, hairline, card shadow; fills its container */
  card: PropTypes.bool,
  /** 'cover' fills the frame; 'contain' centres a cut-out on a white well */
  imageFit: PropTypes.oneOf(['cover', 'contain']),
  /** Link type: the arrow after the label */
  showArrow: PropTypes.bool,
  /** Image frame ratio — '3 / 2' or '3:2' (default 276 / 221) */
  imageAspectRatio: PropTypes.string,
  /** Additional CSS class */
  className: PropTypes.string,
};

export default NavigationCard;
